import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useRef,
} from "react";
import { useToast } from "@/hooks/use-toast";
import { validateToken, logoutUser, ApiError } from "@/services/authService";
import {
  saveAuthData,
  getUserData,
  getToken,
  clearAuthData,
  isAuthenticated,
  UserData,
} from "@/services/tokenService";

interface AuthContextType {
  user: UserData | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, userData: UserData) => void;
  logout: () => Promise<void>;
  validateAuth: () => Promise<boolean>;
  updateUserPermissions: (permissions: any[]) => void;
  updateUserProfile: (profileId: string, profileName: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasValidated, setHasValidated] = useState(false);

  // Toast para avisar sessão expirada
  const { toast, dismiss } = useToast();
  const hasShownExpiryToastRef = useRef(false);

  // Inicializar usuário dos cookies se existir
  useEffect(() => {
    const initializeUser = () => {
      try {
        const userData = getUserData();
        if (userData) {
          setUser(userData);
        }
      } catch (error) {
        // Ignorar erro ao inicializar usuário
      }
    };

    initializeUser();
  }, []);

  // Função para fazer login
  const login = (token: string, userData: UserData) => {
    saveAuthData(token, userData);
    setUser(userData);
  };

  // Função para fazer logout
  const logout = async () => {
    setIsLoading(true);

    // Capturar o token atual para a chamada à API, mas limpar estado local já
    let token: string | null = null;
    try {
      token = getToken();
    } catch {}

    try {
      // Limpar dados locais imediatamente para evitar que useEffects disparem
      // novas cargas com base em estados anteriores
      try { clearAuthData(); } catch {}
      setUser(null);
      setHasValidated(false);

      // Chamar API de logout de forma melhor-esforço usando o token capturado
      if (token) {
        try {
          const result = await logoutUser(token);
          if (
            result &&
            result.success === false &&
            result.error?.code === "TOKEN_BLACKLISTED"
          ) {
            console.log("Token já estava invalidado, continuando com logout local");
          }
        } catch (error: any) {
          if (
            error?.code === "TOKEN_BLACKLISTED" ||
            error?.error?.code === "TOKEN_BLACKLISTED"
          ) {
            console.log("Token já estava invalidado, continuando com logout local");
          } else {
            // Outros erros são ignorados silenciosamente
            console.error("Erro ao fazer logout na API:", error);
          }
        }
      }
    } catch (error) {
      // Continua com o logout local mesmo se a API falhar
      console.error("Erro no logout:", error);
    } finally {
      // Garantir que estado de loading seja liberado
      setIsLoading(false);
    }
  };

  // Função para atualizar as permissões do usuário
  const updateUserPermissions = (permissions: any[]) => {
    if (!user) return;

    const updatedUser = {
      ...user,
      permissions,
    };

    setUser(updatedUser);
    const token = getToken();
    if (token) {
      saveAuthData(token, updatedUser);
    }
  };

  // Função para atualizar o perfil do usuário
  const updateUserProfile = (profileId: string, profileName: string) => {
    if (!user) return;

    const updatedUser = {
      ...user,
      profile: {
        id: profileId,
        name: profileName,
      },
      profile_id: profileId,
      profile_name: profileName,
    };

    setUser(updatedUser);
    const token = getToken();
    if (token) {
      saveAuthData(token, updatedUser);
    }
  };

  // Função para validar autenticação
  const validateAuth = async (): Promise<boolean> => {
    try {
      const token = getToken();
      const userData = getUserData();

      if (!token || !userData) {
        return false;
      }

      // Validar token com a API
      const response = await validateToken(token);

      if (!response.success) {
        // Se o token foi invalidado (logout realizado), fazer logout silencioso
        if (response.error?.code === "TOKEN_BLACKLISTED" || response.error?.code === "TOKEN_EXPIRED" || response.error?.code === "INVALID_TOKEN" || response.error?.code === "USER_DELETED") {
          // Token foi invalidado, fazer logout local sem chamar a API novamente
          clearAuthData();
          setUser(null);
          setHasValidated(false);
          return false;
        }

        // Se a validação falhou mas temos token e dados válidos nos cookies,
        // manter logado (pode ser erro de rede temporário)
        if (token && userData && userData.id && userData.email) {
          return true;
        }
        return false;
      }

      // A API retorna { success: true, data: { user, token, expires_in } }
      // Não há campo 'valid', então verificamos se há dados do usuário
      if (!response.data?.user) {
        // Se não há dados na resposta mas temos dados válidos nos cookies, manter logado
        if (token && userData && userData.id && userData.email) {
          return true;
        }
        return false;
      }

      // Atualizar dados do usuário se necessário
      if (response.data?.user) {
        const currentUser = getUserData();
        const nextUser = response.data.user as UserData;
        const currentPerms = JSON.stringify(currentUser?.permissions || []);
        const nextPerms = JSON.stringify(nextUser.permissions || []);
        const shouldRefresh =
          !currentUser ||
          currentUser.id !== nextUser.id ||
          currentUser.profile?.id !== nextUser.profile?.id ||
          currentUser.profile?.name !== nextUser.profile?.name ||
          currentPerms !== nextPerms;

        if (shouldRefresh) {
          setUser(nextUser);
          saveAuthData(token, nextUser);
        }
      }

      return true;
    } catch (error) {
      // Se houve erro de rede mas temos dados válidos nos cookies, manter logado
      const token = getToken();
      const userData = getUserData();
      if (token && userData && userData.id && userData.email) {
        return true;
      }
      return false;
    }
  };

  // Verificar autenticação ao carregar a aplicação
  useEffect(() => {
    if (hasValidated) return; // Evita validação múltipla

    const checkAuth = async () => {
      setIsLoading(true);

      try {
        // Verificar se existe token e dados do usuário nos cookies
        const userData = getUserData();
        const token = getToken();

        if (userData && token) {
          // Se temos dados válidos nos cookies, definir o usuário primeiro
          // Isso garante que o usuário fica logado mesmo se a validação da API falhar
          if (!user) {
            setUser(userData);
          }

          // Tentar validar com a API (sem bloquear se falhar)
          try {
            const isValid = await validateAuth();
            // Se a validação retornou false (token invalidado), o validateAuth já fez logout
            // Não precisamos fazer nada aqui
            if (!isValid && !getToken()) {
              // Token foi invalidado e dados foram limpos, não continuar
              return;
            }
            // Se a validação foi bem-sucedida, os dados já foram atualizados
            // Se falhou mas ainda temos token, mantém os dados dos cookies (já definidos acima)
          } catch (error) {
            // Em caso de erro, verificar se o token ainda existe
            // Se não existe mais, significa que foi invalidado
            if (!getToken()) {
              return; // Logout já foi feito
            }
            // Se ainda temos token, mantém os dados dos cookies
            // Não faz logout se temos dados válidos
          }
        } else {
          // Se não há dados nos cookies, limpar estado
          if (user) {
            setUser(null);
          }
        }
      } catch (error) {
        // Em caso de erro inesperado, verificar se temos dados nos cookies
        const userData = getUserData();
        const token = getToken();
        if (!userData || !token) {
          // Só limpar estado se realmente não há dados
          if (user) {
            setUser(null);
          }
        }
      } finally {
        setIsLoading(false);
        setHasValidated(true);
      }
    };

    checkAuth();
  }, [hasValidated]); // Removido 'user' das dependências para evitar loop

  // Global 401 handler: listen to 'auth:expired' event fired by fetch401Interceptor
  useEffect(() => {
    const onAuthExpired = () => {
      // Exibir toast de sessão expirada (deduplicado)
      if (!hasShownExpiryToastRef.current) {
        // Fecha toasts existentes e agenda o toast de sessão expirada logo em seguida
        try { dismiss(); } catch {}
        window.setTimeout(() => {
          toast({
            title: "Sessão expirada",
            description: "Sua sessão expirou. Por favor, realize o login novamente.",
            variant: "default",
            duration: 6000,
          });
        }, 50);
        hasShownExpiryToastRef.current = true;
        window.setTimeout(() => {
          hasShownExpiryToastRef.current = false;
        }, 8000);
      }

      // Logout local imediato (evita chamar API novamente após 401)
      try {
        clearAuthData();
      } catch {}
      setUser(null);
      setHasValidated(false);
      setIsLoading(false);
    };

    window.addEventListener("auth:expired", onAuthExpired as EventListener);
    return () => {
      window.removeEventListener("auth:expired", onAuthExpired as EventListener);
    };
  }, [user, toast]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user && !!getToken(),
    isLoading,
    login,
    logout,
    validateAuth,
    updateUserPermissions,
    updateUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
}
